import { BidiModule, DIR_DOCUMENT } from '@angular/cdk/bidi';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from './layout/layout.module';
import { JwtInterceptor, JwtModule, JwtModuleOptions } from '@auth0/angular-jwt';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { environment } from './../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { FilterModule } from '@features/filter/filter.module';
import { AppComponent } from './app.component/app.component';
import { TokenInterceptor } from '@core/interceptor/token.interceptor';
import { LocaleIdHandlerUtil } from '@shared/utils/locale-id-handler.util';
import { SharedModule } from '@shared/shared.module';
import { WrapperComponent } from './wrapper/wrapper.component';
import { registerLocaleData } from '@angular/common';
import en from '@angular/common/locales/en';
import { FormsModule } from '@angular/forms';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireMessagingModule } from '@angular/fire/compat/messaging';
import { MessagingService } from '@core/services/messaging.service';
import { NotificationsModule } from '@features/notifications/notifications.module';
import { SharedFeaturesModule } from '@shared-features/shared-features.module';
import { appConfigFactory } from '@core/services/app-config.service';
import { DynamicScriptService } from '@core/services/dynamic-script.service';
import { TraceModule, TraceService } from '@sentry/angular-ivy';
import { Router } from '@angular/router';
import { ApiKeyInterceptor } from '@core/interceptor/api-key.interceptor';
import { FeatureFlagService } from '@shared/services/feature-flag.service';
import { UserApi } from '@shared-features/services';
import { StorageConstant } from '@shared/constants/storage.constant';
import { SentryErrorHandlerService } from '@core/services/sentry-error-handler.service';
import { RecaptchaV3Module, RECAPTCHA_V3_SITE_KEY, RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha';
import { AmplitudeService, BaseAPI, UserDeviceService } from '@core/services';
import { ErrorHandlerInterceptor } from '@core/interceptor/error-handler.interceptor';
import { FleetSurveyInterceptor } from '@core/interceptor/fleet-survey-interceptor';
import { VendorConfigService } from '@core/services/vendor-source-service.service';

registerLocaleData(en);

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/locales/', '.json');
}

export function getToken(): string {
  try {
    const token = localStorage.getItem(StorageConstant.AUTH_USER);
    if (token) {
      return JSON.parse(token)?.accessToken;
    }
  } catch (e) {
    console.warn('localStorage blocked (sandboxed iframe, strict browser privacy settings)', e);
  }
  return null;
}

const JWT_Module_Options: JwtModuleOptions = {
  config: {
    tokenGetter: getToken,
    allowedDomains: environment.white_listed_Domains,
    headerName: 'Authorization',
    authScheme: 'Bearer ',
  },
};

@NgModule({
  declarations: [AppComponent, WrapperComponent],
  imports: [
    TraceModule,
    HttpClientModule,
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BidiModule,
    JwtModule.forRoot(JWT_Module_Options),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    SharedModule,
    LayoutModule,
    FilterModule,
    FormsModule,
    AngularFireMessagingModule,
    AngularFireModule.initializeApp(environment.firebase),
    SharedFeaturesModule,
    NotificationsModule,
    RecaptchaV3Module,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useClass: SentryErrorHandlerService,
    },
    {
      provide: TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (VendorConfigService: VendorConfigService) => () => VendorConfigService.init(),
      deps: [VendorConfigService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appConfigFactory,
      deps: [
        DynamicScriptService,
        FeatureFlagService,
        UserApi,
        BaseAPI,
        AmplitudeService,
        UserDeviceService,
      ],
      multi: true,
    },
    TranslateService,
    { provide: DIR_DOCUMENT, useValue: 'rtl' },
    { provide: LOCALE_ID, useClass: LocaleIdHandlerUtil, deps: [TranslateService] },

    // ================= Interceptors =================
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiKeyInterceptor,
      multi: true,
    },
    JwtInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorHandlerInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FleetSurveyInterceptor,
      multi: true,
    },
    MessagingService,
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptchaV3SiteKey },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: environment.recaptchaV2SiteKey } as RecaptchaSettings,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
